<template>
  <a-modal
    :visible="open"
    :title="`Configurar \&quot;Enviar como\&quot; - ${record.associate || ''}`"
    width="650px"
    centered
    :footer="null"
    @cancel="handleClose"
    :maskClosable="false"
  >
    <div class="send-as-container">
      <p class="description">
        Agrega hasta 5 correos que tus contactos verán como remitente al recibir un mensaje. El identificador original sigue siendo el que envía y recibe las respuestas.
      </p>

      <div class="header-section">
        <span class="count-label">
          <strong>Correos electrónicos</strong> {{ aliases.length }} / 5
        </span>
        
        <a-tooltip
          :title="addButtonTooltipTitle"
          placement="top"
        >
          <div style="width: 100%">
            <a-button
              type="dashed"
              block
              class="add-btn"
              :disabled="isAddButtonDisabled"
              @click="!isAddButtonDisabled && startAdding()"
            >
              <PlusOutlined /> Agregar correo
            </a-button>
          </div>
        </a-tooltip>
      </div>

      <a-spin :spinning="loading">
        <!-- Empty State -->
        <div v-if="showEmptyState" class="empty-state">
          <div class="empty-illustration">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M54 12H10C7.79 12 6.01 13.79 6.01 16L6 48C6 50.21 7.79 52 10 52H54C56.21 52 58 50.21 58 48V16C58 13.79 56.21 12 54 12ZM54 48H10V20L32 33.75L54 20V48ZM32 29.5L10 15.75H54L32 29.5Z" fill="#D9D9D9"/>
            </svg>
          </div>
          <p class="empty-text">
            Aún no tienes correos configurados. Crea uno para personalizar el remitente de los correos.
          </p>
        </div>

        <!-- List of Aliases (Horizontal Rows) -->
        <div v-if="showList || isAdding" class="alias-list">
          <div v-for="(alias, index) in aliases" :key="alias.id" class="alias-item-row">
            <div class="row-label">
              <span class="required">*</span> Correo {{ index + 1 }}
            </div>
            
            <div class="input-container">
              <div class="input-wrapper">
                <a-input
                  v-if="editingId === alias.id"
                  v-model:value="editingEmail"
                  placeholder="Ingresar correo electrónico"
                  :class="{ 'input-error': editingErrorMsg }"
                  :status="editingErrorMsg ? 'error' : ''"
                  @pressEnter="isEditingEmailValid && saveEdit(alias)"
                  @input="handleInputEditingEmail"
                  @blur="handleBlurEditingEmail"
                >
                  <template #suffix>
                    <CloseCircleFilled v-if="editingErrorMsg" style="color: #ff4d4f" />
                  </template>
                </a-input>
                <a-input
                  v-else
                  :value="alias.email"
                  disabled
                  class="disabled-input"
                />
                <span v-if="editingId === alias.id && editingErrorMsg" class="error-text">
                  {{ editingErrorMsg }}
                </span>
              </div>

              <div class="actions">
                <!-- If Editing this row -->
                <template v-if="editingId === alias.id">
                  <a-tooltip :title="!isEditingEmailValid ? 'Completa el campo con un correo electrónico válido.' : 'Guardar'">
                    <span style="display: inline-block">
                      <a-button type="text" class="btn-check" @click="saveEdit(alias)" :disabled="!isEditingEmailValid">
                        <CheckOutlined />
                      </a-button>
                    </span>
                  </a-tooltip>
                  <a-tooltip title="Cancelar">
                    <a-button type="text" class="btn-close" @click="cancelEditingRow">
                      <CloseOutlined />
                    </a-button>
                  </a-tooltip>
                </template>
                <!-- If Not Editing -->
                <template v-else>
                  <a-tooltip :title="!isChannelActive ? 'Activa el identificador para editar el correo.' : 'Editar'">
                    <span style="display: inline-block">
                      <a-button type="text" class="btn-edit" @click="startEditingRow(alias)" :disabled="isAdding || editingId !== null || !isChannelActive">
                        <EditOutlined />
                      </a-button>
                    </span>
                  </a-tooltip>
                  <a-tooltip :title="!isChannelActive ? 'Activa el identificador para eliminar el correo.' : 'Eliminar'">
                    <span style="display: inline-block">
                      <a-button type="text" class="btn-delete" @click="confirmDelete(alias)" :disabled="isAdding || editingId !== null || !isChannelActive">
                        <DeleteOutlined />
                      </a-button>
                    </span>
                  </a-tooltip>
                </template>
              </div>
            </div>
          </div>

          <!-- Adding Row Form (Horizontal Row) -->
          <div v-if="isAdding" class="alias-item-row adding-row" style="margin-top: 8px;">
            <div class="row-label">
              <span class="required">*</span> Correo {{ aliases.length + 1 }}
            </div>
            <div class="input-container">
              <div class="input-wrapper">
                <a-input
                  ref="inputRef"
                  v-model:value="newEmail"
                  placeholder="Ingresar correo electrónico"
                  :class="{ 'input-error': errorMsg }"
                  :status="errorMsg ? 'error' : ''"
                  @pressEnter="isNewEmailValid && saveNewAlias()"
                  @input="handleInputNewEmail"
                  @blur="handleBlurNewEmail"
                >
                  <template #suffix>
                    <CloseCircleFilled v-if="errorMsg" style="color: #ff4d4f" />
                  </template>
                </a-input>
                <span v-if="errorMsg" class="error-text">{{ errorMsg }}</span>
              </div>
              <div class="actions">
                <a-tooltip :title="!isNewEmailValid ? 'Completa el campo obligatorio para guardar.' : 'Guardar'">
                  <span style="display: inline-block">
                    <a-button type="text" class="btn-check" @click="saveNewAlias" :disabled="!isNewEmailValid">
                      <CheckOutlined />
                    </a-button>
                  </span>
                </a-tooltip>
                <a-tooltip title="Cancelar">
                  <a-button type="text" class="btn-close" @click="cancelAdding">
                    <CloseOutlined />
                  </a-button>
                </a-tooltip>
              </div>
            </div>
          </div>
        </div>
      </a-spin>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, onMounted, nextTick, computed, createVNode } from "vue";
import { useStore } from "vuex";
import { message, Modal } from "ant-design-vue";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  CloseCircleFilled,
  ExclamationCircleOutlined,
} from "@ant-design/icons-vue";
import {
  fetchSendAsAliases,
  createSendAsAlias,
  deleteSendAsAlias,
  updateSendAsAlias,
  fetchSendAsAliasUsage,
} from "@/app/channels/services";
import { channelStatus } from "@/app/channels/utils/constants";

export default defineComponent({
  name: "SendAsConfigModal",
  components: {
    PlusOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
    CloseCircleFilled,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const store = useStore();
    const aliases = ref([]);
    const loading = ref(false);
    const isAdding = ref(false);
    const newEmail = ref("");
    const inputRef = ref(null);

    // Edit row state
    const editingId = ref(null);
    const editingEmail = ref("");

    const isNewEmailDirty = ref(false);
    const backendError = ref("");

    const isEditingEmailDirty = ref(false);
    const editingBackendError = ref("");

    const isChannelActive = computed(() => {
      return props.record?.status === channelStatus.active;
    });

    const canAdd = computed(() => {
      return aliases.value.length < 5;
    });

    const isAddButtonDisabled = computed(() => {
      return !isChannelActive.value || aliases.value.length >= 5 || isAdding.value || editingId.value !== null;
    });

    const addButtonTooltipTitle = computed(() => {
      if (!isChannelActive.value) {
        return "Activa el identificador para agregar un correo.";
      }
      if (aliases.value.length >= 5) {
        return "Alcanzaste el límite de correos electrónicos.";
      }
      return "";
    });

    const showEmptyState = computed(() => {
      return aliases.value.length === 0 && !isAdding.value;
    });

    const showList = computed(() => {
      return aliases.value.length > 0;
    });

    const isNewEmailValid = computed(() => {
      const emailVal = newEmail.value.trim();
      if (!emailVal) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) return false;
      if (emailVal.toLowerCase() === props.record.associate?.toLowerCase()) return false;
      if (aliases.value.some((a) => a.email.toLowerCase() === emailVal.toLowerCase())) return false;
      return true;
    });

    const isEditingEmailValid = computed(() => {
      const emailVal = editingEmail.value.trim();
      if (!emailVal) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) return false;
      if (emailVal.toLowerCase() === props.record.associate?.toLowerCase()) return false;
      if (aliases.value.some((a) => a.id !== editingId.value && a.email.toLowerCase() === emailVal.toLowerCase())) return false;
      return true;
    });

    const errorMsg = computed(() => {
      if (backendError.value) return backendError.value;
      if (!isNewEmailDirty.value) return "";
      const emailVal = newEmail.value.trim();
      if (!emailVal) {
        return "Campo requerido";
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) {
        return "Correo inválido. Revisa que el correo esté bien escrito.";
      }
      if (emailVal.toLowerCase() === props.record.associate?.toLowerCase()) {
        return "Este correo se utiliza para el identificador. Ingresa uno diferente.";
      }
      if (aliases.value.some((a) => a.email.toLowerCase() === emailVal.toLowerCase())) {
        return "Este correo ya se agregó a este identificador. Ingresa uno diferente.";
      }
      return "";
    });

    const editingErrorMsg = computed(() => {
      if (editingBackendError.value) return editingBackendError.value;
      if (!isEditingEmailDirty.value) return "";
      const emailVal = editingEmail.value.trim();
      if (!emailVal) {
        return "Campo requerido";
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) {
        return "Correo inválido. Revisa que el correo esté bien escrito.";
      }
      if (emailVal.toLowerCase() === props.record.associate?.toLowerCase()) {
        return "Este correo se utiliza para el identificador. Ingresa uno diferente.";
      }
      if (aliases.value.some((a) => a.id !== editingId.value && a.email.toLowerCase() === emailVal.toLowerCase())) {
        return "Este correo ya se agregó a este identificador. Ingresa uno diferente.";
      }
      return "";
    });

    const loadAliases = async () => {
      loading.value = true;
      try {
        const data = await fetchSendAsAliases(props.record.id);
        const fetched = Array.isArray(data) ? data : (data?.results || []);
        aliases.value = fetched.slice().sort((a, b) => a.email.localeCompare(b.email));
      } catch (error) {
        console.error("Error loading aliases:", error);
        message.error("No se pudieron cargar los correos de 'Enviar como'");
      } finally {
        loading.value = false;
      }
    };

    const startAdding = () => {
      isAdding.value = true;
      newEmail.value = "";
      isNewEmailDirty.value = false;
      backendError.value = "";
      nextTick(() => {
        if (inputRef.value) {
          inputRef.value.focus();
        }
      });
    };

    const hasUnsavedChanges = () => {
      if (isAdding.value && newEmail.value.trim() !== "") {
        return true;
      }
      if (editingId.value !== null) {
        const original = aliases.value.find((a) => a.id === editingId.value)?.email || "";
        if (editingEmail.value.trim() !== original.trim()) {
          return true;
        }
      }
      return false;
    };

    const cancelAdding = () => {
      if (newEmail.value.trim() !== "") {
        Modal.confirm({
          title: "¿Deseas descartar el progreso realizado?",
          content: "Al descartar, no se guardarán los datos previamente configurados.",
          okText: "Sí, descartar",
          cancelText: "No, seguir completando",
          icon: createVNode(ExclamationCircleOutlined, { style: "color: #faad14;" }),
          centered: true,
          onOk() {
            isAdding.value = false;
            newEmail.value = "";
            isNewEmailDirty.value = false;
            backendError.value = "";
          },
        });
      } else {
        isAdding.value = false;
        newEmail.value = "";
        isNewEmailDirty.value = false;
        backendError.value = "";
      }
    };

    const handleInputNewEmail = () => {
      isNewEmailDirty.value = true;
      backendError.value = "";
    };

    const handleBlurNewEmail = () => {
      isNewEmailDirty.value = true;
    };

    const startEditingRow = (alias) => {
      editingId.value = alias.id;
      editingEmail.value = alias.email;
      isEditingEmailDirty.value = false;
      editingBackendError.value = "";
    };

    const cancelEditingRow = () => {
      const original = aliases.value.find((a) => a.id === editingId.value)?.email || "";
      if (editingEmail.value.trim() !== original.trim()) {
        Modal.confirm({
          title: "¿Deseas descartar el progreso realizado?",
          content: "Al descartar, no se guardarán los datos previamente configurados.",
          okText: "Sí, descartar",
          cancelText: "No, seguir completando",
          icon: createVNode(ExclamationCircleOutlined, { style: "color: #faad14;" }),
          centered: true,
          onOk() {
            editingId.value = null;
            editingEmail.value = "";
            isEditingEmailDirty.value = false;
            editingBackendError.value = "";
          },
        });
      } else {
        editingId.value = null;
        editingEmail.value = "";
        isEditingEmailDirty.value = false;
        editingBackendError.value = "";
      }
    };

    const handleInputEditingEmail = () => {
      isEditingEmailDirty.value = true;
      editingBackendError.value = "";
    };

    const handleBlurEditingEmail = () => {
      isEditingEmailDirty.value = true;
    };

    const validateEmail = (email) => {
      if (!email) {
        return "El correo electrónico es obligatorio";
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return "Ingresa un correo electrónico válido";
      }
      if (email.toLowerCase() === props.record.associate?.toLowerCase()) {
        return "El correo no puede ser igual al identificador principal";
      }
      if (aliases.value.some((a) => a.email.toLowerCase() === email.toLowerCase())) {
        return "Este correo ya se agregó a este identificador. Ingresa uno diferente.";
      }
      return "";
    };

    const validateEditEmail = (email, currentAliasId) => {
      if (!email) {
        return "El correo electrónico es obligatorio";
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return "Ingresa un correo electrónico válido";
      }
      if (email.toLowerCase() === props.record.associate?.toLowerCase()) {
        return "El correo no puede ser igual al identificador principal";
      }
      if (aliases.value.some((a) => a.id !== currentAliasId && a.email.toLowerCase() === email.toLowerCase())) {
        return "Este correo ya se agregó a este identificador. Ingresa uno diferente.";
      }
      return "";
    };

    const saveNewAlias = async () => {
      const emailVal = newEmail.value.trim();
      const validationError = validateEmail(emailVal);
      if (validationError) {
        errorMsg.value = validationError;
        return;
      }

      loading.value = true;
      try {
        await createSendAsAlias(props.record.id, { email: emailVal });
        message.success("Correo agregado con éxito");
        isAdding.value = false;
        newEmail.value = "";
        await loadAliases();
      } catch (error) {
        console.error("Error saving alias:", error);
        let backendErrorMsg = "No se pudo agregar el correo";
        if (error?.data?.email) {
          backendErrorMsg = Array.isArray(error.data.email) ? error.data.email[0] : error.data.email;
        } else if (error?.data?.non_field_errors) {
          backendErrorMsg = error.data.non_field_errors[0];
        } else if (error?.message) {
          backendErrorMsg = error.message;
        }
        backendError.value = backendErrorMsg;
      } finally {
        loading.value = false;
      }
    };

    const checkAndCleanDraftAfterAliasChange = (aliasEmail) => {
      try {
        const userId = localStorage.getItem("used_id");
        if (!userId) return;

        const draftsStr = localStorage.getItem("email_draft");
        if (!draftsStr) return;

        const drafts = JSON.parse(draftsStr);
        const draft = drafts[userId];
        if (!draft) return;

        const matchesChannel = Number(draft.fromEmail) === Number(props.record.id);
        const matchesAlias = draft.sendAsAlias && draft.sendAsAlias.toLowerCase() === aliasEmail.toLowerCase();

        if (matchesChannel && matchesAlias) {
          const hasTo = draft.selectedItems && draft.selectedItems.length > 0;
          const hasCC = draft.selectedItemsCC && draft.selectedItemsCC.length > 0;
          const hasCCO = draft.selectedItemsCCO && draft.selectedItemsCCO.length > 0;
          const hasSubject = draft.subject && draft.subject.trim().length > 0;
          const cleanText = (draft.text || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
          const hasText = cleanText.length > 0;
          const hasFiles = draft.files && draft.files.length > 0;

          const isOnlyDE = !hasTo && !hasCC && !hasCCO && !hasSubject && !hasText && !hasFiles;

          if (isOnlyDE) {
            delete drafts[userId];
            localStorage.setItem("email_draft", JSON.stringify(drafts));
            store.dispatch("clearDraftFiles");
          }
        }
      } catch (error) {
        console.error("Error cleaning draft after alias change:", error);
      }
    };

    const saveEdit = async (alias) => {
      const emailVal = editingEmail.value.trim();
      if (emailVal.toLowerCase() === alias.email.toLowerCase()) {
        editingId.value = null;
        editingEmail.value = "";
        isEditingEmailDirty.value = false;
        editingBackendError.value = "";
        return;
      }

      const validationError = validateEditEmail(emailVal, alias.id);
      if (validationError) {
        editingErrorMsg.value = validationError;
        return;
      }

      loading.value = true;
      let usedInThreads = false;
      try {
        const usageData = await fetchSendAsAliasUsage(props.record.id, alias.id);
        usedInThreads = usageData?.used_in_threads || false;
      } catch (error) {
        console.error("Error checking alias usage:", error);
      } finally {
        loading.value = false;
      }

      const performUpdate = async () => {
        loading.value = true;
        try {
          await updateSendAsAlias(props.record.id, alias.id, { email: emailVal });
          checkAndCleanDraftAfterAliasChange(alias.email);
          message.success("Cambios guardados con éxito");
          editingId.value = null;
          editingEmail.value = "";
          await loadAliases();
        } catch (error) {
          console.error("Error updating alias:", error);
          let backendErrorMsg = "No se pudo actualizar el correo";
          if (error?.data?.email) {
            backendErrorMsg = Array.isArray(error.data.email) ? error.data.email[0] : error.data.email;
          } else if (error?.data?.non_field_errors) {
            backendErrorMsg = error.data.non_field_errors[0];
          } else if (error?.message) {
            backendErrorMsg = error.message;
          }
          editingBackendError.value = backendErrorMsg;
        } finally {
          loading.value = false;
        }
      };

      if (!usedInThreads) {
        await performUpdate();
      } else {
        const content = createVNode("div", {}, [
          createVNode("p", { style: "margin-bottom: 8px;" }, "Al guardar los cambios sucederá lo siguiente:"),
          createVNode("ul", { style: "padding-left: 20px; list-style-type: disc;" }, [
            createVNode("li", { style: "margin-bottom: 4px;" }, [
              "Todos los correos enviados con el antiguo \"Enviar como\" ",
              createVNode("strong", {}, "conservarán el mismo remitente"),
              ".",
            ]),
            createVNode("li", { style: "margin-bottom: 4px;" }, "El correo con los cambios aplicados estará disponible para los próximos envíos y respuestas."),
            createVNode("li", {}, [
              "Las secuencias activas que usen este \"Enviar como\", ",
              createVNode("strong", {}, "conservarán el mismo remitente hasta ser inactivadas"),
              ". Luego de esto, el identificador se limpiará y deberás seleccionar uno nuevo.",
            ]),
          ]),
        ]);

        Modal.confirm({
          title: "¿Deseas guardar los cambios realizados?",
          content,
          okText: "Sí, guardar",
          cancelText: "No, seguir editando",
          icon: createVNode(ExclamationCircleOutlined, { style: "color: #faad14;" }),
          centered: true,
          onOk: performUpdate,
        });
      }
    };

    const confirmDelete = async (alias) => {
      try {
        loading.value = true;
        await deleteSendAsAlias(props.record.id, alias.id, false);
        checkAndCleanDraftAfterAliasChange(alias.email);
        message.success("Correo eliminado con éxito");
        await loadAliases();
      } catch (error) {
        console.error("Error deleting alias:", error);
        const errStr = JSON.stringify(error?.data || error?.message || "");
        if (errStr.includes("confirm=true") || errStr.includes("confirm")) {
          const content = createVNode("div", {}, [
            createVNode("p", { style: "margin-bottom: 8px;" }, "Al eliminar sucederá lo siguiente:"),
            createVNode("ul", { style: "padding-left: 20px; list-style-type: disc;" }, [
              createVNode("li", { style: "margin-bottom: 4px;" }, [
                "El correo dejará de estar disponible para próximos envíos y respuestas. ",
                createVNode("strong", {}, "Esta acción no se podrá deshacer"),
                ".",
              ]),
              createVNode("li", { style: "margin-bottom: 4px;" }, [
                "Todos los correos enviados con el antiguo \"Enviar como\" ",
                createVNode("strong", {}, "conservarán el mismo remitente"),
                ".",
              ]),
              createVNode("li", {}, [
                "Las secuencias activas que usen este \"Enviar como\", ",
                createVNode("strong", {}, "conservarán el mismo remitente hasta ser inactivadas"),
                ". Luego de esto, el identificador se limpiará y deberás seleccionar uno nuevo.",
              ]),
            ]),
          ]);

          Modal.confirm({
            title: 'Estás a punto de eliminar este correo de "Enviar como", ¿deseas continuar?',
            content,
            okText: "Sí, eliminar",
            okButtonProps: { type: "primary", danger: true },
            cancelText: "No, cancelar",
            icon: createVNode(ExclamationCircleOutlined, { style: "color: #faad14;" }),
            centered: true,
            async onOk() {
              try {
                loading.value = true;
                await deleteSendAsAlias(props.record.id, alias.id, true);
                checkAndCleanDraftAfterAliasChange(alias.email);
                message.success("Correo eliminado con éxito");
                await loadAliases();
              } catch (forceError) {
                console.error("Error in force delete:", forceError);
                message.error("No se pudo forzar la eliminación del correo");
              } finally {
                loading.value = false;
              }
            },
          });
        } else {
          message.error("No se pudo eliminar el correo");
        }
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      if (hasUnsavedChanges()) {
        Modal.confirm({
          title: "¿Deseas descartar el progreso realizado?",
          content: "Al descartar, no se guardarán los datos previamente configurados.",
          okText: "Sí, descartar",
          cancelText: "No, seguir completando",
          icon: createVNode(ExclamationCircleOutlined, { style: "color: #faad14;" }),
          centered: true,
          onOk() {
            emit("close");
          },
        });
      } else {
        emit("close");
      }
    };

    onMounted(() => {
      loadAliases();
    });

    return {
      aliases,
      loading,
      isAdding,
      newEmail,
      errorMsg,
      inputRef,
      editingId,
      editingEmail,
      editingErrorMsg,
      canAdd,
      isChannelActive,
      showEmptyState,
      showList,
      isNewEmailValid,
      isEditingEmailValid,
      startAdding,
      cancelAdding,
      handleInputNewEmail,
      handleBlurNewEmail,
      startEditingRow,
      cancelEditingRow,
      handleInputEditingEmail,
      handleBlurEditingEmail,
      saveNewAlias,
      saveEdit,
      confirmDelete,
      handleClose,
      isAddButtonDisabled,
      addButtonTooltipTitle,
    };
  },
});
</script>

<style lang="sass" scoped>
.send-as-container
  padding: 10px 0
  height: 486px
  display: flex
  flex-direction: column

  :deep(.ant-spin-nested-loading)
    flex: 1
    display: flex
    flex-direction: column
    min-height: 0

  :deep(.ant-spin-container)
    flex: 1
    display: flex
    flex-direction: column
    min-height: 0
  
  .description
    color: rgba(0, 0, 0, 0.45)
    margin-bottom: 24px
    font-size: 14px
    line-height: 1.5

  .header-section
    margin-bottom: 20px

    .count-label
      font-size: 14px
      color: rgba(0, 0, 0, 0.85)
      display: block
      margin-bottom: 12px
      
      strong
        font-weight: 600

    .add-btn
      height: 40px
      border-radius: 4px
      font-size: 14px
      color: #1890ff
      border-color: #1890ff
      display: flex
      align-items: center
      justify-content: center
      gap: 8px

      &[disabled], &[disabled]:hover
        color: rgba(0, 0, 0, 0.25) !important
        border-color: #d9d9d9 !important
        background: transparent !important
        cursor: not-allowed !important

  .empty-state
    flex: 1
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    border: 1px solid #f0f0f0
    background-color: #fafafa
    border-radius: 8px
    margin-bottom: 0
    padding: 40px 20px

    .empty-illustration
      margin-bottom: 16px
      display: flex
      justify-content: center
      align-items: center

    .empty-text
      color: rgba(0, 0, 0, 0.45)
      font-size: 14px
      text-align: center
      max-width: 360px
      margin: 0
      line-height: 1.5

  .alias-list
    flex: 1
    overflow-y: auto
    display: flex
    flex-direction: column
    gap: 8px
    padding-right: 4px

  .alias-item-row
    display: flex
    align-items: center
    gap: 16px
    margin-bottom: 12px


    .row-label
      min-width: 80px
      font-size: 14px
      color: rgba(0, 0, 0, 0.85)
      
      .required
        color: #ff4d4f
        margin-right: 4px

    .input-container
      flex-grow: 1
      display: flex
      align-items: center
      gap: 12px

      .input-wrapper
        flex-grow: 1
        display: flex
        flex-direction: column

        .disabled-input
          &:deep(input)
            background-color: rgba(0, 0, 0, 0.04) !important
            color: rgba(0, 0, 0, 0.45) !important
            cursor: not-allowed !important
          &:deep(.ant-input-affix-wrapper)
            background-color: rgba(0, 0, 0, 0.04) !important
            cursor: not-allowed !important

        .input-error
          border-color: #ff4d4f
          box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2)

        .error-text
          color: #ff4d4f
          font-size: 12px
          margin-top: 4px

      .actions
        display: flex
        gap: 4px
        
        .btn-check
          color: #52c41a
          font-size: 18px
          padding: 4px 8px
          display: flex
          align-items: center
          justify-content: center
          
          &:hover
            color: #73d13d

          &[disabled]
            cursor: not-allowed !important
            color: rgba(0, 0, 0, 0.25) !important

        .btn-close
          color: #ff4d4f
          font-size: 18px
          padding: 4px 8px
          display: flex
          align-items: center
          justify-content: center
          
          &:hover
            color: #ff7875

        .btn-edit
          color: #1890ff
          font-size: 18px
          padding: 4px 8px
          display: flex
          align-items: center
          justify-content: center
          
          &:hover
            color: #40a9ff

          &[disabled], &[disabled]:hover
            color: rgba(0, 0, 0, 0.25) !important
            cursor: not-allowed !important

        .btn-delete
          color: #ff4d4f
          font-size: 18px
          padding: 4px 8px
          display: flex
          align-items: center
          justify-content: center
          
          &:hover
            color: #ff7875

          &[disabled], &[disabled]:hover
            color: rgba(0, 0, 0, 0.25) !important
            cursor: not-allowed !important
</style>
